import { Avatar, Dropdown, Flex, MenuProps } from "antd";
import { FC, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router";

import { logout, useAuth } from "@/app/AuthProvider";
import { DEFAULT_ADMIN_ROUTE, DEFAULT_SETTINGS_ROUTE } from "@/app/Routes";

import { HeaderMenu } from "@/components//HeaderMenu";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useBreadcrumbs } from "@/components/Breadcrumbs/BreadcrumbsContext";

import ArrowsUpDownIcon from "@/assets/arrows-up-down.svg?react";
import MenuCloseIcon from "@/assets/close.svg?react";
import ImageIcon from "@/assets/image.svg?react";
import LogoutIcon from "@/assets/logout.svg?react";
import MenuStripesIcon from "@/assets/menu-stripes.svg?react";
import SettingsIcon from "@/assets/settings.svg?react";

import "./index.less";

const titleMap: Record<string, string> = {
  courses: "Courses",
  dashboard: "Dashboard",
  course: "Courses",
  programs: "Programs",
  users: "Users",
  create: "Create",
  edit: "Edit",
  "create-notification": "Create Notification",
  "edit-notification": "Edit Notification",
  view: "View",
  posts: "Posts",
  notifications: "Custom Notifications",
  "user-report": "User Reports",
  "activity-log": "Activity Log",
};

export const Header: FC = () => {
  const { pathname } = useLocation();
  const { avatar } = useAuth();
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumbs();

  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);

  const role = localStorage.getItem("role");
  const isCreator = useMemo(() => role?.toLowerCase()?.includes("creator"), [role]);
  const isAdmin = useMemo(() => role?.toLowerCase()?.includes("admin"), [role]);

  const title = useMemo(() => {
    if (breadcrumbs.length > 0) {
      const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
      return lastCrumb.label;
    }

    const segments = pathname.split("/").filter(Boolean);

    for (let i = segments.length - 1; i >= 0; i--) {
      const key = segments[i];
      if (titleMap[key]) return titleMap[key];
    }

    return undefined;
  }, [breadcrumbs, pathname]);

  const profileItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "settings",
        label: (
          <Flex align="center" justify="space-between">
            <span>Settings</span>
            <SettingsIcon />
          </Flex>
        ),
        onClick: () => navigate(DEFAULT_SETTINGS_ROUTE),
      },
      isAdmin || isCreator
        ? {
            key: "switchToLearning",
            label: (
              <Flex align="center" justify="space-between">
                <span>Switch to Admin</span>
                <ArrowsUpDownIcon />
              </Flex>
            ),
            onClick: () => navigate(DEFAULT_ADMIN_ROUTE),
          }
        : null,
      {
        key: "logout",
        label: (
          <Flex align="center" justify="space-between">
            <span>Logout</span>
            <LogoutIcon />
          </Flex>
        ),
        onClick: logout,
      },
    ],
    [isAdmin, isCreator, navigate]
  );

  return (
    <Flex rootClassName="header-container" justify="space-between" align="center">
      <Helmet>
        <title>{title ? `CONNECT | ${title}` : `CONNECT`}</title>
        {title && <meta name="description" content={`${title} page`} />}
      </Helmet>

      {breadcrumbs.length ? <Breadcrumbs breadcrumbs={breadcrumbs} /> : <span className="history-placeholder"></span>}

      {isMenuVisible ? (
        <MenuCloseIcon className="menu-button" onClick={() => setIsMenuVisible(false)} />
      ) : (
        <MenuStripesIcon className="menu-button" onClick={() => setIsMenuVisible(true)} />
      )}

      <Flex className="profile-avatar-wrapper" justify="right" align="center" gap={28}>
        <Dropdown menu={{ items: profileItems }} placement="bottomRight" overlayStyle={{ width: 200 }}>
          {avatar ? <Avatar className="profile-avatar" src={avatar} /> : <Avatar className="profile-avatar" icon={<ImageIcon />} />}
        </Dropdown>
      </Flex>

      <HeaderMenu isVisible={isMenuVisible} setIsVisible={() => setIsMenuVisible(false)} />
    </Flex>
  );
};
